import React, { useState, useRef, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { LoadingSpinner } from '../Loading'
import { backgroundRemovalAPI } from '../../services/api'

const BackgroundRemovalTool = () => {
  const [image, setImage] = useState(null)
  const [processedImage, setProcessedImage] = useState(null)
  const [dualResults, setDualResults] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [processingMode, setProcessingMode] = useState('dual') // 'single' | 'dual' | 'bulk'
  // Bulk mode state
  const [bulkItems, setBulkItems] = useState([]) // [{id,name,size,preview,status,resultUrl,resultSize,resultFilename,error}]
  const [bulkProcessing, setBulkProcessing] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 })
  const fileInputRef = useRef(null)
  const bulkFileInputRef = useRef(null)
  const canvasRef = useRef(null)

  // Handle file upload
  const handleFileUpload = useCallback(async (files) => {
    const file = files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file (JPEG, PNG, etc.)')
      return
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setError('File size must be less than 10MB')
      return
    }

    setError(null)
    setProcessedImage(null)

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setImage({
        file,
        preview: e.target.result,
        name: file.name,
        size: file.size
      })
    }
    reader.readAsDataURL(file)

    // Auto-process the image
    await processImage(file)
  }, [])

  // Process image for background removal
  const processImage = async (file) => {
    if (!file) return

    setIsProcessing(true)
    setError(null)
    setProcessedImage(null)
    setDualResults(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      if (processingMode === 'dual') {
        // Use dual processing endpoint
        const response = await backgroundRemovalAPI.dualProcess(formData)
        const data = response.data

        if (data.success) {
          setDualResults(data.data)
        } else {
          throw new Error(data.message || 'Dual processing failed')
        }
      } else {
        // Use single processing endpoint (original behavior)
        const response = await backgroundRemovalAPI.upload(formData)

        // Get the processed image as blob
        const blob = response.data
        const processedUrl = URL.createObjectURL(blob)

        // Get filename from headers
        const contentDisposition = response.headers['content-disposition']
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `bg_removed_${file.name}`

        setProcessedImage({
          url: processedUrl,
          filename: filename,
          size: blob.size,
          blob: blob
        })
      }

    } catch (err) {
      console.error('Background removal error:', err)
      setError(err.message || 'Failed to remove background. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle paste from clipboard
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) {
          await handleFileUpload([file])
        }
        break
      }
    }
  }, [handleFileUpload])

  // ---- Bulk upload handlers ----

  const MAX_SIZE = 10 * 1024 * 1024 // 10MB per file

  // Add selected/dropped files to the bulk queue (validates type + size).
  const addBulkFiles = useCallback((files) => {
    const incoming = Array.from(files || [])
    if (incoming.length === 0) return

    const accepted = []
    const rejected = []
    incoming.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        rejected.push(`${file.name} (not an image)`)
      } else if (file.size > MAX_SIZE) {
        rejected.push(`${file.name} (over 10MB)`)
      } else {
        accepted.push(file)
      }
    })

    if (rejected.length > 0) {
      setError(`Skipped ${rejected.length} file(s): ${rejected.join(', ')}`)
    } else {
      setError(null)
    }

    accepted.forEach((file) => {
      const id = `${file.name}_${file.size}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const item = {
        id,
        file,
        name: file.name,
        size: file.size,
        preview: null,
        status: 'pending', // pending | processing | done | error
        resultUrl: null,
        resultSize: 0,
        resultFilename: null,
        error: null,
      }
      // Generate a preview thumbnail.
      const reader = new FileReader()
      reader.onload = (e) => {
        setBulkItems((prev) => prev.map((it) => (it.id === id ? { ...it, preview: e.target.result } : it)))
      }
      reader.readAsDataURL(file)
      setBulkItems((prev) => [...prev, item])
    })
  }, [])

  // Process the whole queue one image at a time.
  const processBulk = useCallback(async () => {
    // Snapshot the items that still need processing.
    const toProcess = bulkItems.filter((it) => it.status === 'pending' || it.status === 'error')
    if (toProcess.length === 0) return

    setBulkProcessing(true)
    setError(null)
    setBulkProgress({ done: 0, total: toProcess.length })

    let done = 0
    for (const item of toProcess) {
      // Mark as processing.
      setBulkItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, status: 'processing', error: null } : it)))

      try {
        const formData = new FormData()
        formData.append('file', item.file)
        const response = await backgroundRemovalAPI.upload(formData)

        const blob = response.data
        const url = URL.createObjectURL(blob)
        const contentDisposition = response.headers?.['content-disposition']
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `bg_removed_${item.name}`

        setBulkItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? { ...it, status: 'done', resultUrl: url, resultSize: blob.size, resultFilename: filename }
              : it
          )
        )
      } catch (err) {
        console.error('Bulk background removal error:', err)
        setBulkItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? { ...it, status: 'error', error: err.message || 'Processing failed' }
              : it
          )
        )
      } finally {
        done += 1
        setBulkProgress({ done, total: toProcess.length })
      }
    }

    setBulkProcessing(false)
  }, [bulkItems])

  // Remove a single item from the queue (and clean up its object URL).
  const removeBulkItem = (id) => {
    setBulkItems((prev) => {
      const target = prev.find((it) => it.id === id)
      if (target?.resultUrl) URL.revokeObjectURL(target.resultUrl)
      return prev.filter((it) => it.id !== id)
    })
  }

  // Clear the whole bulk queue.
  const resetBulk = () => {
    bulkItems.forEach((it) => { if (it.resultUrl) URL.revokeObjectURL(it.resultUrl) })
    setBulkItems([])
    setBulkProgress({ done: 0, total: 0 })
    setBulkProcessing(false)
    setError(null)
  }

  // Download every successfully processed image.
  const downloadAllBulk = () => {
    bulkItems
      .filter((it) => it.status === 'done' && it.resultUrl)
      .forEach((it, idx) => {
        // Stagger downloads slightly so the browser doesn't drop them.
        setTimeout(() => downloadImageFromUrl(it.resultUrl, it.resultFilename || `bg_removed_${it.name}`), idx * 250)
      })
  }

  // Setup dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.bmp', '.tiff']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  // Download processed image
  const downloadImage = () => {
    if (!processedImage) return

    const link = document.createElement('a')
    link.href = processedImage.url
    link.download = processedImage.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Reset tool
  const resetTool = () => {
    setImage(null)
    setProcessedImage(null)
    setDualResults(null)
    setError(null)
    setIsProcessing(false)

    // Clean up object URLs
    if (processedImage?.url) {
      URL.revokeObjectURL(processedImage.url)
    }
  }

  // Download image from URL
  const downloadImageFromUrl = (url, filename) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // View image in fullsize modal
  const viewImageFullsize = (url, title) => {
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50'
    modal.onclick = () => document.body.removeChild(modal)

    const img = document.createElement('img')
    img.src = url
    img.className = 'max-w-full max-h-full object-contain'
    img.alt = title

    const closeBtn = document.createElement('button')
    closeBtn.innerHTML = '✕'
    closeBtn.className = 'absolute top-4 right-4 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75'
    closeBtn.onclick = () => document.body.removeChild(modal)

    modal.appendChild(img)
    modal.appendChild(closeBtn)
    document.body.appendChild(modal)
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-2xl shadow-lg">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          AI Background Removal
        </h1>
        <p className="text-gray-600">
          Remove backgrounds from your images instantly using advanced AI technology
        </p>
      </div>

      {/* Processing Mode Selector */}
      <div className="flex justify-center mb-6">
        <div className="bg-gray-100 rounded-lg p-1 flex">
          <button
            onClick={() => setProcessingMode('dual')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              processingMode === 'dual'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🤖 Dual AI Processing
          </button>
          <button
            onClick={() => setProcessingMode('single')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              processingMode === 'single'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ⚡ Single Processing
          </button>
          <button
            onClick={() => setProcessingMode('bulk')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              processingMode === 'bulk'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📦 Bulk Upload
          </button>
        </div>
      </div>

      {/* Mode Description */}
      <div className="text-center mb-6">
        {processingMode === 'dual' ? (
          <p className="text-sm text-gray-600">
            🎯 Compare results from two different AI models and choose the best one
          </p>
        ) : processingMode === 'bulk' ? (
          <p className="text-sm text-gray-600">
            📦 Upload many images at once — each is processed one by one with live progress
          </p>
        ) : (
          <p className="text-sm text-gray-600">
            ⚡ Fast processing with our BiRefNet-Portrait AI model
          </p>
        )}
      </div>

      {/* ================= BULK MODE ================= */}
      {processingMode === 'bulk' && (
        <div className="space-y-6">
          {/* Bulk upload area */}
          <div
            className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center transition-all duration-300 hover:border-gray-400 hover:bg-gray-50"
            onDragOver={(e) => { e.preventDefault() }}
            onDrop={(e) => { e.preventDefault(); addBulkFiles(e.dataTransfer.files) }}
          >
            <input
              ref={bulkFileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { addBulkFiles(e.target.files); e.target.value = '' }}
            />
            <div className="text-5xl mb-3">🗂️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Upload multiple images</h3>
            <p className="text-gray-600 mb-4">Drag &amp; drop images here, or choose files. They are processed one by one.</p>
            <button
              onClick={() => bulkFileInputRef.current?.click()}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Choose Files
            </button>
            <div className="text-sm text-gray-500 mt-3">
              Supports JPEG, PNG, WEBP, BMP, TIFF • Max 10MB each
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <div className="text-amber-500">⚠️</div>
              <p className="text-amber-800 text-sm">{error}</p>
            </div>
          )}

          {/* Queue toolbar */}
          {bulkItems.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{bulkItems.length}</span> image(s) •{' '}
                <span className="text-green-600">{bulkItems.filter((i) => i.status === 'done').length} done</span> •{' '}
                <span className="text-blue-600">{bulkItems.filter((i) => i.status === 'processing').length} processing</span> •{' '}
                <span className="text-gray-500">{bulkItems.filter((i) => i.status === 'pending').length} pending</span>
                {bulkItems.filter((i) => i.status === 'error').length > 0 && (
                  <> • <span className="text-red-600">{bulkItems.filter((i) => i.status === 'error').length} failed</span></>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={processBulk}
                  disabled={bulkProcessing || bulkItems.every((i) => i.status === 'done' || i.status === 'processing')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkProcessing
                    ? `Processing ${bulkProgress.done}/${bulkProgress.total}...`
                    : bulkItems.some((i) => i.status === 'error')
                      ? 'Process pending / retry failed'
                      : 'Remove backgrounds'}
                </button>
                <button
                  onClick={downloadAllBulk}
                  disabled={!bulkItems.some((i) => i.status === 'done')}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Download all
                </button>
                <button
                  onClick={resetBulk}
                  disabled={bulkProcessing}
                  className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Overall progress bar */}
          {bulkProcessing && bulkProgress.total > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
              />
            </div>
          )}

          {/* Items grid */}
          {bulkItems.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bulkItems.map((it) => (
                <div key={it.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                  <div
                    className="relative aspect-square bg-gray-100 flex items-center justify-center"
                    style={it.status === 'done' ? {
                      backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                      backgroundSize: '20px 20px',
                      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                    } : undefined}
                  >
                    {/* Show result when done, else the original preview */}
                    {it.status === 'done' && it.resultUrl ? (
                      <img src={it.resultUrl} alt={it.name} className="w-full h-full object-contain" />
                    ) : it.preview ? (
                      <img src={it.preview} alt={it.name} className="w-full h-full object-contain opacity-90" />
                    ) : (
                      <div className="text-4xl">🖼️</div>
                    )}

                    {/* Processing overlay */}
                    {it.status === 'processing' && (
                      <div className="absolute inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center">
                        <LoadingSpinner size="md" color="blue" />
                        <p className="text-xs text-gray-600 mt-2">Removing background...</p>
                      </div>
                    )}

                    {/* Status badge */}
                    <div className="absolute top-2 left-2">
                      {it.status === 'pending' && <span className="bg-gray-700 text-white text-xs px-2 py-1 rounded">Pending</span>}
                      {it.status === 'processing' && <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">Processing</span>}
                      {it.status === 'done' && <span className="bg-green-600 text-white text-xs px-2 py-1 rounded">✓ Done</span>}
                      {it.status === 'error' && <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">Failed</span>}
                    </div>

                    {/* Remove button */}
                    {!bulkProcessing && (
                      <button
                        onClick={() => removeBulkItem(it.id)}
                        className="absolute top-2 right-2 bg-black bg-opacity-50 text-white w-7 h-7 rounded-full flex items-center justify-center hover:bg-opacity-70"
                        title="Remove"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="p-3 space-y-2">
                    <p className="text-sm font-medium text-gray-900 truncate" title={it.name}>{it.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(it.size)}
                      {it.status === 'done' && it.resultSize ? ` → ${formatFileSize(it.resultSize)}` : ''}
                    </p>
                    {it.status === 'error' && (
                      <p className="text-xs text-red-600">{it.error}</p>
                    )}
                    <div className="flex gap-2">
                      {it.status === 'done' && it.resultUrl && (
                        <>
                          <button
                            onClick={() => viewImageFullsize(it.resultUrl, it.name)}
                            className="flex-1 bg-gray-100 text-gray-700 text-xs py-2 rounded hover:bg-gray-200 transition-colors"
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => downloadImageFromUrl(it.resultUrl, it.resultFilename || `bg_removed_${it.name}`)}
                            className="flex-1 bg-green-600 text-white text-xs py-2 rounded hover:bg-green-700 transition-colors"
                          >
                            ⬇️ Download
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload Area */}
      {processingMode !== 'bulk' && !image && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
            isDragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
          onPaste={handlePaste}
          tabIndex={0}
        >
          <input {...getInputProps()} ref={fileInputRef} />
          
          <div className="space-y-4">
            <div className="text-6xl">🖼️</div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Upload or Paste Your Image
              </h3>
              <p className="text-gray-600 mb-4">
                Drag & drop an image here, click to browse, or paste from clipboard (Ctrl+V)
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Choose File
                </button>
                <span className="text-gray-500">or paste with Ctrl+V</span>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              Supports JPEG, PNG, WEBP, BMP, TIFF • Max 10MB
            </div>
          </div>
        </div>
      )}

      {/* Processing/Results Area */}
      {processingMode !== 'bulk' && image && (
        <div className="space-y-6">
          {/* Image Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original Image */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Original</h3>
              <div className="relative bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={image.preview}
                  alt="Original"
                  className="w-full h-auto max-h-96 object-contain"
                />
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                  {formatFileSize(image.size)}
                </div>
              </div>
            </div>

            {/* Results Section */}
            {processingMode === 'dual' ? (
              /* Dual Results */
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">AI Processing Results</h3>

                {isProcessing ? (
                  <div className="text-center py-12">
                    <LoadingSpinner size="lg" color="blue" />
                    <p className="text-gray-600 mt-4">
                      Processing with dual AI models...
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      This may take 10-15 seconds
                    </p>
                  </div>
                ) : dualResults ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Local AI Result */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">🤖 Local AI Model</h4>
                        {dualResults.summary?.local_success ? (
                          <span className="text-green-600 text-sm">✅ Success</span>
                        ) : (
                          <span className="text-red-600 text-sm">❌ Failed</span>
                        )}
                      </div>
                      <div className="relative bg-gray-100 rounded-xl overflow-hidden min-h-[200px] flex items-center justify-center">
                        {dualResults.results?.local ? (
                          <>
                            <img
                              src={dualResults.results.local.url}
                              alt="Local AI Result"
                              className="w-full h-auto max-h-64 object-contain"
                              style={{
                                backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                                backgroundSize: '20px 20px',
                                backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                              }}
                            />
                            <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                              BiRefNet • {Math.round(dualResults.results.local.size / 1024)}KB
                            </div>
                            <div className="absolute top-2 right-2 flex space-x-2">
                              <button
                                onClick={() => viewImageFullsize(dualResults.results.local.url, 'Local AI Result')}
                                className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                                title="View Local Result Fullsize"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => downloadImageFromUrl(dualResults.results.local.url, `local_${image.name}`)}
                                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                                title="Download Local Result"
                              >
                                ⬇️
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-4xl mb-2">❌</div>
                            <p className="text-gray-500">Local processing failed</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* External API Result */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">🌐 External API</h4>
                        {dualResults.summary?.external_success ? (
                          <span className="text-green-600 text-sm">✅ Success</span>
                        ) : (
                          <span className="text-red-600 text-sm">❌ Failed</span>
                        )}
                      </div>
                      <div className="relative bg-gray-100 rounded-xl overflow-hidden min-h-[200px] flex items-center justify-center">
                        {dualResults.results?.external ? (
                          <>
                            <img
                              src={dualResults.results.external.url}
                              alt="External API Result"
                              className="w-full h-auto max-h-64 object-contain"
                              style={{
                                backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                                backgroundSize: '20px 20px',
                                backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                              }}
                            />
                            <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                              External • {Math.round(dualResults.results.external.size / 1024)}KB
                            </div>
                            <div className="absolute top-2 right-2 flex space-x-2">
                              <button
                                onClick={() => viewImageFullsize(dualResults.results.external.url, 'External AI Result')}
                                className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                                title="View External Result Fullsize"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => downloadImageFromUrl(dualResults.results.external.url, `external_${image.name}`)}
                                className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition-colors"
                                title="Download External Result"
                              >
                                ⬇️
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-4xl mb-2">❌</div>
                            <p className="text-gray-500">External processing failed</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">❌</div>
                    <p className="text-red-600 font-medium">Processing Failed</p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">⏳</div>
                    <p className="text-gray-500">Waiting to process...</p>
                  </div>
                )}
              </div>
            ) : (
              /* Single Result */
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">Background Removed</h3>
                <div className="relative bg-gray-100 rounded-xl overflow-hidden min-h-[200px] flex items-center justify-center">
                  {isProcessing ? (
                    <div className="text-center py-12">
                      <LoadingSpinner size="lg" color="blue" />
                      <p className="text-gray-600 mt-4">
                        Removing background...
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        This may take a few seconds
                      </p>
                    </div>
                  ) : processedImage ? (
                    <>
                      <img
                        src={processedImage.url}
                        alt="Background Removed"
                        className="w-full h-auto max-h-96 object-contain"
                        style={{
                          backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                          backgroundSize: '20px 20px',
                          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                        }}
                      />
                      <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                        {formatFileSize(processedImage.size)}
                      </div>
                    </>
                  ) : error ? (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-4">❌</div>
                      <p className="text-red-600 font-medium">Processing Failed</p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-4">⏳</div>
                      <p className="text-gray-500">Waiting to process...</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-red-500 mr-3">⚠️</div>
                <div>
                  <h4 className="text-red-800 font-medium">Error</h4>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* Single Mode Download */}
            {processedImage && processingMode === 'single' && (
              <button
                onClick={downloadImage}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download Image</span>
              </button>
            )}

            {/* Dual Mode Download All */}
            {dualResults && processingMode === 'dual' && (dualResults.results?.local || dualResults.results?.external) && (
              <button
                onClick={() => {
                  if (dualResults.results?.local) {
                    downloadImageFromUrl(dualResults.results.local.url, `local_${image.name}`)
                  }
                  if (dualResults.results?.external) {
                    downloadImageFromUrl(dualResults.results.external.url, `external_${image.name}`)
                  }
                }}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download All Results</span>
              </button>
            )}

            <button
              onClick={resetTool}
              className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Upload New Image
            </button>

            {image && !isProcessing && (
              <button
                onClick={() => processImage(image.file)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Process Again
              </button>
            )}
          </div>

          {/* File Info */}
          <div className="text-center text-sm text-gray-500">
            <p>Original: {image.name}</p>
            {processingMode === 'single' && processedImage && (
              <p>Processed: {processedImage.filename}</p>
            )}
            {processingMode === 'dual' && dualResults && (
              <div className="space-y-1">
                {dualResults.summary?.local_success && (
                  <p>✅ Local AI: BiRefNet-Portrait</p>
                )}
                {dualResults.summary?.external_success && (
                  <p>✅ External API: Cloud Processing</p>
                )}
                <p>Success Rate: {dualResults.summary?.successful_methods || 0}/2 methods</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default BackgroundRemovalTool
